import { trackFileDownload } from './analyticsUtils';

/*
 * Hand a document to the device's share sheet (AirDrop, Messages, Mail, Save to Files,
 * Print on iOS; the equivalents on Android). The file is fetched and shared as a File so
 * the recipient gets the PDF itself, not a link that needs sign-in or a network.
 *
 * Where the share sheet cannot take files (desktop Safari, Firefox, older browsers) the
 * same call falls back to a download, so one button works everywhere.
 *
 * Returns 'shared' | 'downloaded' | 'cancelled' | 'failed'.
 */

const fileNameFrom = (url, title) => {
  const fromUrl = decodeURIComponent(url.split('?')[0].split('/').pop() || '');
  if (/\.pdf$/i.test(fromUrl)) return fromUrl;
  const base = (title || 'document').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
  return `${base}.pdf`;
};

const download = (blob, name) => {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
};

export const canShareFiles = () => typeof navigator.share === 'function' && typeof navigator.canShare === 'function';

export const shareFile = async (url, title) => {
  const name = fileNameFrom(url, title);
  trackFileDownload(name, 'document', url);
  let blob;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    blob = await res.blob();
  } catch {
    // Could not fetch the bytes (offline, blocked): let the browser open it instead
    window.open(url, '_blank', 'noopener,noreferrer');
    return 'failed';
  }
  const file = new File([blob], name, { type: blob.type || 'application/pdf' });
  if (canShareFiles() && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: title || name });
      return 'shared';
    } catch (err) {
      if (err && err.name === 'AbortError') return 'cancelled';
      // Share failed for another reason: fall through to download
    }
  }
  download(blob, name);
  return 'downloaded';
};
